import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Trash2, Eye, HardHat, FileText } from "lucide-react";
import api, { apiError, downloadBlob } from "@/lib/api";
import DocPreview from "@/components/DocPreview";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function fmtSize(bytes) {
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes > 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

export default function OhsTab({ project, docs, refreshDocs }) {
  const { readOnly } = useAuth();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef();

  const ohsDocs = docs.filter((d) => d.category === "OHS");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "OHS");
      fd.append("linked_to", "ohs");
      await api.post(`/projects/${project.id}/documents`, fd);
      toast.success(`${file.name} uploaded to OHS`);
      refreshDocs();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove OHS document "${doc.filename}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("OHS document removed");
      refreshDocs();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="ohs-tab" className="bdx-page-enter">
      <div className="mb-5 flex flex-col gap-4 border-b border-gray-300 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="bdx-icon-tile"><HardHat size={17} strokeWidth={1.9} /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-gray-950">OHS — Site Safety Documents</h2>
            <p className="mt-1 text-[11px] text-gray-500">Project-specific Occupational Health &amp; Safety document repository — stored in durable cloud storage</p>
          </div>
        </div>
        {!readOnly && (
          <>
            <input ref={inputRef} type="file" className="hidden" data-testid="ohs-file-input" onChange={(e) => upload(e.target.files[0])} />
            <button data-testid="ohs-upload-btn" className="bdx-btn-primary" disabled={busy} onClick={() => inputRef.current?.click()}>
              <Upload size={15} strokeWidth={1.8} /> {busy ? "Uploading…" : "Upload OHS Document"}
            </button>
          </>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-[0_3px_12px_rgba(16,19,21,0.055)]">
        <table className="bdx-table" data-testid="ohs-table">
          <thead><tr><th>Document</th><th>Size</th><th>Uploaded By</th><th>Uploaded</th><th className="w-28">Actions</th></tr></thead>
          <tbody>
            {ohsDocs.length === 0 && <tr><td colSpan={5} className="text-center text-gray-400 py-10">No OHS documents uploaded yet</td></tr>}
            {ohsDocs.map((d) => (
              <tr key={d.id}>
                <td><div className="flex min-w-[240px] items-center gap-3"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#d9cde5] bg-[#f3eff7] text-[#604184]"><FileText size={14} strokeWidth={1.8} /></span><span className="font-medium text-gray-900">{d.filename}</span></div></td>
                <td className="font-mono-data">{fmtSize(d.size)}</td>
                <td>{d.uploaded_by}</td>
                <td className="font-mono-data text-gray-500">{fmtDateTime(d.uploaded_at)}</td>
                <td>
                  <div className="flex gap-1">
                    <button data-testid={`ohs-view-${d.id}`} className="bdx-icon-btn" title="View" aria-label={`View ${d.filename}`} onClick={() => setPreview(d)}>
                      <Eye size={14} strokeWidth={1.7} />
                    </button>
                    <button data-testid={`ohs-download-${d.id}`} className="bdx-icon-btn" title="Download" aria-label={`Download ${d.filename}`}
                      onClick={() => downloadBlob(`/documents/${d.id}/download`, d.filename)}>
                      <Download size={14} strokeWidth={1.7} />
                    </button>
                    {!readOnly && (
                      <button data-testid={`ohs-delete-${d.id}`} className="bdx-icon-btn-danger" title="Remove" aria-label={`Remove ${d.filename}`} onClick={() => remove(d)}>
                        <Trash2 size={14} strokeWidth={1.7} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
